import { Spin, Typography } from 'antd'
import { Histogram, useColorScale } from 'features/dataLayers'
import { FigureSelectionArea } from 'features/fde'
import { useIndicationData } from 'features/indicationTriage/IndicationContext'
import { useGeckoLocale } from 'features/locale'
import React, { CSSProperties } from 'react'
import {
  GeckoMeasurement,
  GeckoMeasurementRange,
  GeckoMeasurementUnit,
} from 'types'

import { useCroppedCdfSummaryStats } from './useCroppedCdfSummaryStats'

const { Text } = Typography

export interface IndicationThicknessHistogramProps {
  area?: FigureSelectionArea
  refValue?: GeckoMeasurement
}

interface HistogramLabelProps {
  measurementRange: GeckoMeasurementRange
  measurement: GeckoMeasurement
  labelHeight: number
  labelPrefix?: string
}

const HistogramLabel: React.FC<HistogramLabelProps> = ({
  measurementRange: measurementRangeRaw,
  measurement,
  labelHeight,
  labelPrefix,
}) => {
  const unit = measurement.unit
  const measurementRange = measurementRangeRaw.convertTo(unit)
  const measurementRangeLen =
    measurementRange.max.num - measurementRange.min.num
  const measurementFrac =
    (measurement.num - measurementRange.min.num) / measurementRangeLen

  const textStyle: CSSProperties = {
    paddingTop: '3px',
    position: 'absolute',
    whiteSpace: 'nowrap',
    top: `100%`,
  }
  const { formatMeasurement } = useGeckoLocale()
  return (
    <div style={{ position: 'relative', height: '100%' }}>
      <div
        style={{
          position: 'absolute',
          left: `${measurementFrac * 100}%`,
          top: '0%',
          height: `calc(100% + ${labelHeight ?? 10}px)`,
          borderLeft: 'solid #616161 1px',
        }}>
        <span
          style={{
            ...textStyle,
            ...(measurementFrac <= 0.5
              ? { left: '-0.2rem' }
              : { right: '-0.2rem' }),
          }}>
          <Text strong>{labelPrefix ? `${labelPrefix} ` : ''}</Text>
          <span>{formatMeasurement({ measurement: measurement })}</span>
        </span>
      </div>
    </div>
  )
}

/**
 * A component to show the thickness distribution within a specificied area,
 * along with min- and average- markers.
 */
export const IndicationThicknessHistogram: React.FC<
  IndicationThicknessHistogramProps
> = (props) => {
  const {
    colorScaleThreshold: selectedColorThreshold,
    relevantThicknessCDF: cdf,
    componentDataFrames,
  } = useIndicationData()
  const { area: selection } = props

  const {
    stats: cdfStats,
    isLoading: cdfStatsLoading,
    croppedCdf,
    croppedDataAverage,
  } = useCroppedCdfSummaryStats(cdf, selection)

  const { colorScale } = useColorScale(croppedCdf, selectedColorThreshold)

  const fullRange =
    cdfStats?.value_range.convertTo(
      cdf?.data.params.measurement_unit ?? GeckoMeasurementUnit.Millimeters
    ) ??
    GeckoMeasurementRange.CreateFromValues(0, 1, GeckoMeasurementUnit.Inches)

  const { getPreferredMeasurementUnit } = useGeckoLocale()
  const cdfUnit = croppedCdf?.data.params.measurement_unit ?? fullRange.unit
  const preferredMeasUnit = getPreferredMeasurementUnit(cdfUnit)
  return (
    <Spin spinning={cdfStatsLoading}>
      <div
        style={{
          margin: 0,
          padding: 0,
          maxWidth: '280px',
          position: 'relative',
        }}>
        {!cdfStats ? null : (
          <Histogram
            cdfId={croppedCdf?.id ?? componentDataFrames[0].id}
            valueRange={cdfStats.value_range}
            histogram={cdfStats.histogram}
            fullValueRange={fullRange}
            filteredValueRange={fullRange}
            selectedColorScale={colorScale}
          />
        )}
        <HistogramLabel
          measurement={fullRange.min.convertTo(preferredMeasUnit)}
          measurementRange={fullRange}
          labelHeight={30}
          labelPrefix='Min'
        />
        <HistogramLabel
          measurement={GeckoMeasurement.CreateFromValue(
            croppedDataAverage ?? 0,
            cdfUnit
          ).convertTo(preferredMeasUnit)}
          measurementRange={fullRange}
          labelHeight={10}
          labelPrefix='Avg'
        />
        {props.refValue && (
          <HistogramLabel
            measurement={props.refValue.convertTo(preferredMeasUnit)}
            measurementRange={fullRange}
            labelHeight={50}
            labelPrefix='Reference Value'
          />
        )}
      </div>
      <div style={{ height: 'calc(30px + 2.7rem)' }} />
    </Spin>
  )
}
